import { all, takeLatest, call, put, select } from 'redux-saga/effects'
import api from '../../../../services/apiInstance'
import AdLibraryActions, { AdLibraryTypes } from './AdLibraryRedux'
import { mapContentToAd, mapTagToFolder } from '../utils/adapters'
import { DEFAULT_CONTENT_TYPES } from '../../../../services/contents'

// Root saga for the isolated Ad Library store.
//
// Reuses the shared `api` singleton (src/services/Api.js via apiInstance) — the
// SAME methods the global DisplaySagas use. Auth + white-label headers are
// applied automatically by the request transforms in Api.js, so listing and
// mutations are already authenticated. Error handling mirrors DisplaySagas.
//
// `put` here dispatches to the isolated Ad Library store (the one that owns this
// middleware). `api` is passed as the first worker arg (takeLatest(TYPE, worker,
// api)) to match the project convention in src/sagas/index.js.

const toErrors = (resp) => {
  const data = resp && resp.data
  if (resp && resp.problem === 'CLIENT_ERROR' && data) {
    if (data.errors && typeof data.errors === 'object') {
      const messages = Object.keys(data.errors).reduce(
        (acc, key) => acc.concat(Array.isArray(data.errors[key]) ? data.errors[key] : [data.errors[key]]),
        [],
      )
      if (messages.length) return messages
    }
    if (data.message) return [data.message]
  }
  return [resp ? resp.problem : 'UNKNOWN']
}

// Server-side page size for the ads listing (folders are NOT paginated).
const PAGE_SIZE = 24


const SORT_VALUES = ['name', 'oldest', 'newest']

// scope: { id, isCompany }. Advertisers list by company, others by user.
// `type` is the server-side type filter (array); empty/absent => both types.
// `page` is 1-based; the endpoint paginates via start/length (datatable style).
// `sort` is the server-side ordering ('name' | 'oldest' | 'newest'); invalid/absent => default.
function* listContents(api, scope, tag, search, type, page, sort) {
  const current = page || 1
  const params = {
    start: (current - 1) * PAGE_SIZE,
    length: PAGE_SIZE,
    page: current,
    draw: 1,
    datatable: true,
    origin: 'dsp',
    type: type && type.length ? type : DEFAULT_CONTENT_TYPES,
  }
  if (tag) params.tag = tag
  if (search) params.search = search
  if (SORT_VALUES.includes(sort)) params.sort = sort

  const fetch = scope.isCompany ? api.getContents : api.getContentsByUsers
  const resp = yield call(fetch, scope.id, params)
  if (resp.ok) {
    const data = resp.data || {}
    const rows = Array.isArray(data) ? data : data.data || []
    // Datatable convention (mirrors ContentsLibraryComponent): a free-text search
    // narrows the set, so its total is `recordsFiltered`; otherwise `recordsTotal`.
    const total = Array.isArray(data)
      ? data.length
      : (search ? data.recordsFiltered : data.recordsTotal) || rows.length
    yield put(AdLibraryActions.getContentsSuccess(rows.map(mapContentToAd), total))
    return true
  }
  yield put(AdLibraryActions.getContentsFailure(toErrors(resp)))
  return false
}

export function* getContentsRequest(api, action) {
  const { scope, tag, search, types, page, sort } = action
  yield call(listContents, api, scope, tag, search, types, page, sort)
}

export function* getTagsRequest(api, action) {
  const { scope } = action
  const fetch = scope.isCompany ? api.getTagsContents : api.getTagsContentsByUser
  const resp = yield call(fetch, scope.id)
  if (resp.ok) {
    yield put(AdLibraryActions.getTagsSuccess((resp.data || []).map(mapTagToFolder)))
  } else {
    yield put(AdLibraryActions.getTagsFailure(toErrors(resp)))
  }
}

// Create a folder (content-tag). `parentId` (optional) nests it; omit => root.
// On success refetch the folder tree so the new folder shows up.
export function* createFolderRequest(api, action) {
  const { name, parentId } = action
  const body = { name }
  if (parentId) body.parent_id = parentId
  const resp = yield call(api.createContentTag, body)
  if (resp.ok) {
    yield put(AdLibraryActions.createFolderSuccess())
    const scope = yield select((s) => s.contentScope)
    if (scope) yield put(AdLibraryActions.getTagsRequest(scope))
  } else {
    yield put(AdLibraryActions.createFolderFailure(toErrors(resp)))
  }
}

// Rename a folder (content-tag): PUT /contents-tags/{id} with the new name.
// `parent_id` is sent ALWAYS (the folder's current parent) — omitting it makes
// the backend move the folder to the root. On success refetch the folder tree.
export function* renameFolderRequest(api, action) {
  const { id, name, parentId } = action
  const resp = yield call(api.updateContentTag, id, { name, parent_id: parentId == null ? null : parentId })
  if (resp.ok) {
    yield put(AdLibraryActions.renameFolderSuccess())
    const scope = yield select((s) => s.contentScope)
    if (scope) yield put(AdLibraryActions.getTagsRequest(scope))
  } else {
    yield put(AdLibraryActions.renameFolderFailure(toErrors(resp)))
  }
}

// Delete folders (logical, bulk): PUT /contents-tags/archive { ids }. Mirrors
// deleteAdsRequest — all-or-nothing on the backend, so one call handles the whole
// selection (one or many). On success refetch the folder tree.
export function* deleteFoldersRequest(api, action) {
  const { ids } = action
  const resp = yield call(api.archiveContentTags, ids)
  if (resp.ok) {
    yield put(AdLibraryActions.deleteFoldersSuccess())
    const scope = yield select((s) => s.contentScope)
    if (scope) yield put(AdLibraryActions.getTagsRequest(scope))
  } else {
    yield put(AdLibraryActions.deleteFoldersFailure(toErrors(resp)))
  }
}

// Refetch the current listing (scope + tag + search + type + page) after a mutation.
function* refetchCurrentContents(api) {
  const { contentScope, selectedTag, contentSearch, contentType, contentPage, contentSort } = yield select((s) => ({
    contentScope: s.contentScope,
    selectedTag: s.selectedTag,
    contentSearch: s.contentSearch,
    contentType: s.contentType,
    contentPage: s.contentPage,
    contentSort: s.contentSort,
  }))
  if (contentScope)
    yield call(listContents, api, contentScope, selectedTag, contentSearch, contentType, contentPage, contentSort)
}

export function* renameAdRequest(api, action) {
  const { id, name } = action
  const resp = yield call(api.updateContent, id, { name })
  if (resp.ok) {
    yield put(AdLibraryActions.renameAdSuccess())
    yield call(refetchCurrentContents, api)
  } else {
    yield put(AdLibraryActions.renameAdFailure(toErrors(resp)))
  }
}

export function* deleteAdsRequest(api, action) {
  const { ids } = action
  // Logical delete via the bulk archive endpoint: PUT /contents/archive { ids }.
  // All-or-nothing on the backend, so a single call archives the whole selection.
  const resp = yield call(api.archiveContents, ids)
  if (resp.ok) {
    yield put(AdLibraryActions.deleteAdsSuccess())
    yield call(refetchCurrentContents, api)
  } else {
    yield put(AdLibraryActions.deleteAdsFailure(toErrors(resp)))
  }
}

// Upload one file (POST /contents, multipart) and report its individual result
// so the UI can toast it on its own. Never throws — a failed upload resolves to
// `false` so a sibling's failure can't abort the others.
function* uploadOne(api, file, tags, index, customName, categoryId) {
  const resp = yield call(api.createContent, {
    file,
    name: customName || file.name.replace(/\.[^/.]+$/, ''),
    tags,
    categoryId,
  })
  yield put(AdLibraryActions.uploadAdProgress({ index, name: file.name, ok: resp.ok, status: resp.status }))
  return resp.ok
}

// Upload one dynamic ad (DCO): the ad URL travels as `file` and the mandatory
// preview image as `preview`. Reported through the same progress channel as file
// uploads so the UI toasts it identically.
function* uploadDynamicOne(api, dynamic, tags, categoryId) {
  const resp = yield call(api.createContent, {
    file: dynamic.url,
    name: dynamic.name,
    tags,
    type: dynamic.type,
    preview: dynamic.preview,
    urlWhitelist: dynamic.urlWhitelist,
    categoryId,
  })
  yield put(
    AdLibraryActions.uploadAdProgress({ index: 0, name: dynamic.name, ok: resp.ok, status: resp.status }),
  )
  return resp.ok
}

export function* uploadAdsRequest(api, action) {
  const { files, meta } = action
  const list = Array.from(files || [])
  const dynamic = meta && meta.dynamic
  // meta.folderId tags the upload; meta.categoryId applies to every ad in it.
  const tags = meta && meta.folderId ? [meta.folderId] : []
  const categoryId = meta ? meta.categoryId : null

  // A dynamic ad carries no files, so it is one request instead of the per-file fan-out.
  if (dynamic) {
    yield call(uploadDynamicOne, api, dynamic, tags, categoryId)
    yield put(AdLibraryActions.uploadAdsSuccess())
    yield call(refetchCurrentContents, api)
    return
  }

  if (!list.length) {
    yield put(AdLibraryActions.uploadAdsSuccess())
    return
  }
  // POST /contents per file, in parallel. A single-file upload may carry a custom
  // name; bulk uploads keep each file's own name.
  // Each file dispatches its own uploadAdProgress as it finishes (per-file toast).
  const customName = list.length === 1 && meta && meta.name ? meta.name : null
  yield all(list.map((file, index) => call(uploadOne, api, file, tags, index, customName, categoryId)))
  // The flag just needs to clear; per-file outcomes are surfaced via progress.
  yield put(AdLibraryActions.uploadAdsSuccess())
  yield call(refetchCurrentContents, api)
}

// Brands come embedded in each client (client.brands), so there is no brands
// worker — getClients is the only client/brand fetch.
export function* getClientsRequest(api, action) {
  const { userId, search } = action
  const resp = yield call(api.getClients, userId, search ? { search } : undefined)
  if (resp.ok) {
    yield put(AdLibraryActions.getClientsSuccess(resp.data))
  } else {
    yield put(AdLibraryActions.getClientsFailure(toErrors(resp)))
  }
}

// GET /contents/content-categories. Same endpoint the campaign editor uses for its
// category select (DisplaySagas getCategoriesRequest); resp.data is the array.
export function* getCategoriesRequest(api) {
  const resp = yield call(api.getCategories)
  if (resp.ok) {
    yield put(AdLibraryActions.getCategoriesSuccess(resp.data))
  } else {
    yield put(AdLibraryActions.getCategoriesFailure(toErrors(resp)))
  }
}

export default function* rootSaga() {
  yield all([
    takeLatest(AdLibraryTypes.GET_TAGS_REQUEST, getTagsRequest, api),
    takeLatest(AdLibraryTypes.GET_CONTENTS_REQUEST, getContentsRequest, api),
    takeLatest(AdLibraryTypes.RENAME_AD_REQUEST, renameAdRequest, api),
    takeLatest(AdLibraryTypes.DELETE_ADS_REQUEST, deleteAdsRequest, api),
    takeLatest(AdLibraryTypes.UPLOAD_ADS_REQUEST, uploadAdsRequest, api),
    takeLatest(AdLibraryTypes.CREATE_FOLDER_REQUEST, createFolderRequest, api),
    takeLatest(AdLibraryTypes.RENAME_FOLDER_REQUEST, renameFolderRequest, api),
    takeLatest(AdLibraryTypes.DELETE_FOLDERS_REQUEST, deleteFoldersRequest, api),
    takeLatest(AdLibraryTypes.GET_CLIENTS_REQUEST, getClientsRequest, api),
    takeLatest(AdLibraryTypes.GET_CATEGORIES_REQUEST, getCategoriesRequest, api),
  ])
}
